import React, { useCallback, useEffect } from 'react';
import { useProducts } from './useProducts';
import { HomeCatalogShelves, HomeFeaturedShelf } from './HomeDynamicCatalog';
import HomeNextExhibitionBanner from './HomeNextExhibitionBanner';
import { useExhibitions } from './useExhibitions';
import { useSiteSettings } from './SiteSettingsContext';
import PageSeoHelmet from './PageSeoHelmet';
import StorefrontHero from './components/StorefrontHero';
import HomeInstagramSection from './components/home/HomeInstagramSection';
import RecentlyViewedStrip from './recentlyViewed/RecentlyViewedStrip';
import { usePullToRefresh } from './usePullToRefresh';

const Home = () => {
  const { allProducts, refetch: refetchCatalog } = useProducts();
  const exhibitions = useExhibitions();
  const { settings: site, refresh: refreshSettings } = useSiteSettings();

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      refetchCatalog({ silent: true }),
      refreshSettings(),
      exhibitions.refetch(),
    ]);
  }, [exhibitions.refetch, refetchCatalog, refreshSettings]);

  const { pulling, ready, refreshing, pullDistance, threshold } = usePullToRefresh(handleRefresh, {
    threshold: 110,
  });

  const homeCopy = site.page_copy?.home || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="Home-main Home-main--product-first">
      <PageSeoHelmet page="home" />

      {(pulling || refreshing) ? (
        <div
          className={`home-pull-refresh${ready ? ' home-pull-refresh--ready' : ''}${refreshing ? ' home-pull-refresh--active' : ''}`}
          aria-live="polite"
          aria-hidden={!pulling && !refreshing}
          style={{ '--pull-progress': `${Math.min(100, (pullDistance / threshold) * 100)}%` }}
        >
          <span className="home-pull-refresh__label">
            {refreshing ? 'Refreshing…' : ready ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      ) : null}

      {exhibitions.status === 'ready' && exhibitions.upcoming.length > 0 ? (
        <div className="home-top-strips">
          <HomeNextExhibitionBanner upcoming={exhibitions.upcoming} homeCopy={homeCopy} />
        </div>
      ) : null}

      <StorefrontHero compact />

      <HomeFeaturedShelf />

      <HomeCatalogShelves />

      <RecentlyViewedStrip className="recently-viewed home-recently-viewed" />

      <HomeInstagramSection site={site} homeCopy={homeCopy} allProducts={allProducts} />
    </div>
  );
};

export default Home;
